import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Bug,
  Plus,
  Filter,
  Search,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useDefects, useDefectStats } from '../api/useDefects'
import { useProjects } from '@/features/projects/api/useProjects'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  DefectSeverity,
  DefectPriority,
  DefectStatus,
} from '@/types/defect.types'

// ============================================================================
// Helpers
// ============================================================================

const getSeverityColor = (sev: string): string => {
  switch (sev) {
    case 'critical': return 'bg-red-600 text-white'
    case 'high':     return 'bg-orange-500 text-white'
    case 'medium':   return 'bg-yellow-500 text-black'
    case 'low':      return 'bg-blue-500 text-white'
    default:         return 'bg-gray-500 text-white'
  }
}

const getPriorityColor = (pri: string): string => {
  switch (pri) {
    case 'critical': return 'bg-red-600 text-white'
    case 'high':     return 'bg-orange-500 text-white'
    case 'medium':   return 'bg-yellow-500 text-black'
    case 'low':      return 'bg-green-500 text-white'
    default:         return 'bg-gray-500 text-white'
  }
}

const getStatusColor = (st: string): string => {
  switch (st) {
    case 'open':        return 'bg-blue-500 text-white'
    case 'in_progress': return 'bg-purple-500 text-white'
    case 'resolved':    return 'bg-green-500 text-white'
    case 'closed':      return 'bg-gray-500 text-white'
    case 'reopened':    return 'bg-orange-500 text-white'
    case 'deferred':    return 'bg-yellow-500 text-black'
    default:            return 'bg-gray-500 text-white'
  }
}

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const formatStatusLabel = (st: string): string =>
  st.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// ============================================================================
// Component
// ============================================================================

const DefectsPage = () => {
  // Filter state
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all')
  const [status, setStatus] = useState<DefectStatus | 'all'>('all')
  const [severity, setSeverity] = useState<DefectSeverity | 'all'>('all')
  const [priority, setPriority] = useState<DefectPriority | 'all'>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 50

  const projectId =
    selectedProjectId !== 'all' ? parseInt(selectedProjectId, 10) : undefined

  // Data hooks
  const { data: projects } = useProjects()
  const { data: defectsData, isLoading } = useDefects({
    project_id: projectId,
    status: status !== 'all' ? status : undefined,
    severity: severity !== 'all' ? severity : undefined,
    priority: priority !== 'all' ? priority : undefined,
    search: search || undefined,
    limit: pageSize,
    offset: page * pageSize,
  })
  const { data: stats, isLoading: statsLoading } = useDefectStats(projectId)

  // Derived values
  const totalPages = defectsData ? Math.ceil(defectsData.total / pageSize) : 0

  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (selectedProjectId !== 'all') count++
    if (status !== 'all') count++
    if (severity !== 'all') count++
    if (priority !== 'all') count++
    if (search) count++
    return count
  }, [selectedProjectId, status, severity, priority, search])

  // Handlers
  const handleClearFilters = () => {
    setSelectedProjectId('all')
    setStatus('all')
    setSeverity('all')
    setPriority('all')
    setSearch('')
    setPage(0)
  }

  const handleFilterChange = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bug className="h-8 w-8" />
            Defects
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage defects locally or sync with external systems
          </p>
        </div>
        <Button asChild>
          <Link to="/results/defects/new">
            <Plus className="h-4 w-4 mr-2" />
            Report Defect
          </Link>
        </Button>
      </div>

      {/* Statistics Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[88px] rounded-lg" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Defects</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_defects}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Open</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.open_defects}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>In Progress</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {stats.in_progress_defects}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Resolved</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.resolved_defects}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Critical Severity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.critical_defects}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>High Priority</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.high_priority}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="secondary">{activeFiltersCount} active</Badge>
              )}
            </CardTitle>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Project */}
            <div className="space-y-2">
              <Label>Project</Label>
              <Select
                value={selectedProjectId}
                onValueChange={handleFilterChange(setSelectedProjectId)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects?.map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={handleFilterChange<DefectStatus | 'all'>(setStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="reopened">Reopened</SelectItem>
                  <SelectItem value="deferred">Deferred</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Severity */}
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={severity}
                onValueChange={handleFilterChange<DefectSeverity | 'all'>(setSeverity)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={priority}
                onValueChange={handleFilterChange<DefectPriority | 'all'>(setPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search defects..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(0)
                  }}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Defects Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Defects{' '}
              {defectsData && (
                <span className="text-muted-foreground font-normal">
                  ({defectsData.total})
                </span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Loading */}
          {isLoading && (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {/* Empty */}
          {!isLoading && (!defectsData || defectsData.defects.length === 0) && (
            <div className="text-center py-12">
              <Bug className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <h3 className="text-lg font-semibold mb-1">No defects found</h3>
              <p className="text-muted-foreground mb-4">
                {activeFiltersCount > 0
                  ? 'Try adjusting your filters to see more results.'
                  : 'Create your first defect to get started.'}
              </p>
              {activeFiltersCount > 0 ? (
                <Button variant="outline" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/results/defects/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Report Defect
                  </Link>
                </Button>
              )}
            </div>
          )}

          {/* Table */}
          {!isLoading && defectsData && defectsData.defects.length > 0 && (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Defect</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[100px]">Severity</TableHead>
                      <TableHead className="w-[100px]">Priority</TableHead>
                      <TableHead className="w-[140px]">Assigned To</TableHead>
                      <TableHead className="w-[110px]">Created</TableHead>
                      <TableHead className="w-[90px]">Sync</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {defectsData.defects.map((defect) => (
                      <TableRow key={defect.id} className="group">
                        {/* Defect Key */}
                        <TableCell>
                          <Link
                            to={`/results/defects/${defect.id}`}
                            className="font-mono font-medium text-primary hover:underline"
                          >
                            {defect.defect_key}
                          </Link>
                        </TableCell>

                        {/* Title + Project */}
                        <TableCell>
                          <div className="max-w-md">
                            <Link
                              to={`/results/defects/${defect.id}`}
                              className="hover:underline line-clamp-1 font-medium"
                            >
                              {defect.title}
                            </Link>
                            {defect.project_name && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {defect.project_name}
                              </div>
                            )}
                          </div>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Badge className={getStatusColor(defect.status)}>
                            {formatStatusLabel(defect.status)}
                          </Badge>
                        </TableCell>

                        {/* Severity */}
                        <TableCell>
                          <Badge className={getSeverityColor(defect.severity)}>
                            {defect.severity}
                          </Badge>
                        </TableCell>

                        {/* Priority */}
                        <TableCell>
                          <Badge className={getPriorityColor(defect.priority)}>
                            {defect.priority}
                          </Badge>
                        </TableCell>

                        {/* Assigned To */}
                        <TableCell>
                          {defect.assigned_to_name || defect.assigned_to_email ? (
                            <span className="text-sm">
                              {defect.assigned_to_name || defect.assigned_to_email}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Unassigned</span>
                          )}
                        </TableCell>

                        {/* Created */}
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(defect.created_at)}
                        </TableCell>

                        {/* Sync Status */}
                        <TableCell>
                          {defect.external_url ? (
                            <a
                              href={defect.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {defect.sync_status === 'synced'
                                ? 'Synced'
                                : defect.sync_status === 'sync_pending'
                                ? 'Pending'
                                : defect.sync_status === 'sync_failed'
                                ? 'Failed'
                                : defect.sync_status}
                            </a>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              Local
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * pageSize + 1} to{' '}
                    {Math.min((page + 1) * pageSize, defectsData.total)} of{' '}
                    {defectsData.total} defects
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(totalPages - 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default DefectsPage
